import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, delay, map, Observable, throwError } from 'rxjs';
import {
  CreateReportResponse,
  CreateVisualizationPayload,
  FarmerReceiptResponse,
  ReportResponse,
  ScheduleReportPayload,
  ReportDataResponse,
  VisualizationDataResponse,
  VisualizationResponse,
  ReceivingResponse,
  ReconciliationResponse,
  BagTraceabilityResponse,
  TransactionsSummaryResponse,
} from './report.model';
import { PaginationParams } from '../general-store/general.model';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private readonly apiUrl = environment.apiUrl;

  private readonly http = inject(HttpClient);

  public getReports = (paginationParams: PaginationParams): Observable<ReportResponse> => {
    return this.http
      .get<ReportResponse>(`${this.apiUrl}/api/product-reports`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching reports: ' + error.message));
        }),
      );
  };

  public getReport = (id: number): Observable<CreateReportResponse> => {
    return this.http.get<CreateReportResponse>(`${this.apiUrl}/api/product-reports/${id}`).pipe(
      map((response) => response),
      catchError((error) => {
        return throwError(() => new Error('Error fetching report: ' + error.message));
      }),
    );
  };

  public getVisualizationData = (
    reportId: number,
    visualizationId: number,
  ): Observable<VisualizationDataResponse> => {
    return this.http
      .get<VisualizationDataResponse>(
        `${this.apiUrl}/api/product-reports/${reportId}/visualizations/${visualizationId}/data`,
      )
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching visualization data: ' + error.message));
        }),
      );
  };

  public getViewReportData = (
    id: number,
    paginationParams: PaginationParams,
  ): Observable<ReportDataResponse> => {
    return this.http
      .get<ReportDataResponse>(`${this.apiUrl}/api/product-reports/${id}/data`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching view report data: ' + error.message));
        }),
      );
  };

  public getFarmerReceipt = (
    paginationParams: PaginationParams,
  ): Observable<FarmerReceiptResponse> => {
    return this.http
      .get<FarmerReceiptResponse>(`${this.apiUrl}/api/static-reports/farmer-receipt`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching farmer receipt: ' + error.message));
        }),
      );
  };

  public getTransferVoucher = (
    paginationParams: PaginationParams,
  ): Observable<FarmerReceiptResponse> => {
    return this.http
      .get<FarmerReceiptResponse>(`${this.apiUrl}/api/static-reports/transfer-voucher`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching transfer voucher: ' + error.message));
        }),
      );
  };

  public getReceiving = (paginationParams: PaginationParams): Observable<ReceivingResponse> => {
    return this.http
      .get<ReceivingResponse>(`${this.apiUrl}/api/static-reports/receiving`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching receiving: ' + error.message));
        }),
      );
  };

  public getReconciliation = (
    paginationParams: PaginationParams,
  ): Observable<ReconciliationResponse> => {
    return this.http
      .get<ReconciliationResponse>(`${this.apiUrl}/api/static-reports/reconciliation`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching reconciliation: ' + error.message));
        }),
      );
  };

  public getBagTraceability = (
    paginationParams: PaginationParams,
  ): Observable<BagTraceabilityResponse> => {
    return this.http
      .get<BagTraceabilityResponse>(`${this.apiUrl}/api/static-reports/bag-traceability`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching bag traceability: ' + error.message));
        }),
      );
  };

  public getTransactionsSummary = (
    paginationParams: PaginationParams,
  ): Observable<TransactionsSummaryResponse> => {
    return this.http
      .get<TransactionsSummaryResponse>(`${this.apiUrl}/api/static-reports/transactions-summary`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(
            () => new Error('Error fetching transactions summary: ' + error.message),
          );
        }),
      );
  };

  public createReport(payload: any): Observable<CreateReportResponse> {
    return this.http
      .post<CreateReportResponse>(`${this.apiUrl}/api/product-reports/`, payload)
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  public scheduleReport(payload: ScheduleReportPayload): Observable<CreateReportResponse> {
    return this.http
      .post<CreateReportResponse>(`${this.apiUrl}/api/report-schedules/`, payload)
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  public createVisualization(
    id: number,
    payload: CreateVisualizationPayload,
  ): Observable<VisualizationResponse> {
    return this.http
      .post<VisualizationResponse>(
        `${this.apiUrl}/api/product-reports/${id}/visualizations`,
        payload,
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  public updateReport = (id: number, payload: any): Observable<CreateReportResponse> => {
    return this.http
      .put<CreateReportResponse>(`${this.apiUrl}/api/product-reports/${id}`, payload)
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  };

  public updateVisualization = (
    reportId: number,
    visualizationId: number,
    payload: CreateVisualizationPayload,
  ): Observable<VisualizationResponse> => {
    return this.http
      .put<VisualizationResponse>(
        `${this.apiUrl}/api/product-reports/${reportId}/visualizations/${visualizationId}`,
        payload,
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  };

  public deleteReport(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/api/product-reports/${id}`).pipe(
      map((response) => response),
      catchError((error) => {
        return throwError(() => {
          console.log(error);
          return new Error('Error deleting report');
        });
      }),
    );
  }

  public deleteVisualization(reportId: number, visualizationId: number): Observable<void> {
    return this.http
      .delete<void>(
        `${this.apiUrl}/api/product-reports/${reportId}/visualizations/${visualizationId}`,
      )
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => {
            console.log(error);
            return new Error('Error deleting visualization');
          });
        }),
      );
  }

  public exportStaticReport(reportType: string, filters: string): Observable<void> {
    return this.http
      .get<void>(`${this.apiUrl}/api/static-reports/export/${reportType}`, {
        params: {
          filters,
        },
      })
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error exporting static report'));
        }),
      );
  }
}
